import {
  CURRENCIES,
  GENDERS,
  LANGUAGE_PROFICIENCIES,
  LANGUAGES,
  MARITAL_STATUSES,
  PASSPORT_STATUS_LABELS,
  PASSPORT_STATUSES,
  DISTRICTS,
  LINK_SECTION_LABELS,
  publicRegistrationSchema,
  type LinkSection,
  type PublicRegistrationInput,
} from '@crm/shared'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarClock, CheckCircle2, Loader2, Lock, Plus, Trash2, Users } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import { Checkbox, Select } from '@/components/ui/select'
import { countryOptions, educationOptions, jobCategoryOptions } from '@/features/clients/options'
import type { PublicLink } from '@/features/links/api'
import { ApiClientError, apiFetch } from '@/lib/api'

/**
 * The public registration form. Deliberately outside the `_app` route group:
 * no auth guard, no sidebar, no session — a stranger with a link, on a phone.
 */
export const Route = createFileRoute('/r/$slug')({
  component: PublicRegistrationPage,
})

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined

function PublicRegistrationPage() {
  const { slug } = Route.useParams()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['public-link', slug],
    queryFn: () => apiFetch<{ link: PublicLink }>(`/api/public/links/${slug}`),
    retry: false,
  })

  if (isLoading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-[var(--color-muted-foreground)]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      </Shell>
    )
  }

  if (isError || !data) {
    const notFound = error instanceof ApiClientError && error.status === 404
    return (
      <Shell>
        <Notice
          icon={<Lock className="h-8 w-8 text-[var(--color-muted-foreground)]" />}
          title={notFound ? 'Form not found' : 'Something went wrong'}
          body={
            notFound
              ? 'Check the link you were given, or ask the agency for a new one.'
              : 'Please try again in a moment.'
          }
        />
      </Shell>
    )
  }

  const link = data.link
  return (
    <Shell>
      <header className="mb-6">
        <h1 className="text-xl font-semibold">{link.title}</h1>
        {link.description && (
          <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-muted-foreground)]">
            {link.description}
          </p>
        )}
      </header>

      {link.state === 'open' ? (
        <RegistrationForm link={link} slug={slug} />
      ) : (
        <ClosedState link={link} />
      )}
    </Shell>
  )
}

/** Each non-open state gets its own explanation — "closed" alone helps nobody. */
function ClosedState({ link }: { link: PublicLink }) {
  if (link.state === 'scheduled') {
    return (
      <Notice
        icon={<CalendarClock className="h-8 w-8 text-[var(--color-primary)]" />}
        title="Registrations haven’t opened yet"
        body={
          link.opensAt
            ? `This form opens on ${formatWhen(link.opensAt)}. Please come back then.`
            : 'Please check back later.'
        }
      />
    )
  }

  if (link.state === 'capped') {
    return (
      <Notice
        icon={<Users className="h-8 w-8 text-amber-600" />}
        title="This drive is full"
        body="We’ve received the maximum number of registrations. Contact the agency about future openings."
      />
    )
  }

  return (
    <Notice
      icon={<Lock className="h-8 w-8 text-[var(--color-muted-foreground)]" />}
      title="Registrations are closed"
      body={
        link.closesAt
          ? `This form closed on ${formatWhen(link.closesAt)}. Contact the agency about future openings.`
          : 'Contact the agency about future openings.'
      }
    />
  )
}

type Step = 0 | 1 | 2

const STEP_TITLES = ['About you', 'Contact details', 'Work preferences']

function RegistrationForm({ link, slug }: { link: PublicLink; slug: string }) {
  const [step, setStep] = useState<Step>(0)
  const [reference, setReference] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const shows = (section: LinkSection) => link.sections.includes(section)

  const form = useForm<PublicRegistrationInput>({
    resolver: zodResolver(publicRegistrationSchema),
    defaultValues: {
      fullName: '',
      phone: '',
      passportStatus: 'none',
      preferredCountries: [],
      jobCategoryIds: [],
      languages: [],
      foreignExperience: [],
      consentSms: true,
      consentWhatsapp: true,
      consentEmail: false,
    },
    mode: 'onTouched',
  })

  const {
    register,
    control,
    handleSubmit,
    trigger,
    watch,
    formState: { errors },
  } = form

  const languages = useFieldArray({ control, name: 'languages' })
  const foreignExperience = useFieldArray({ control, name: 'foreignExperience' })
  const passportStatus = watch('passportStatus')

  /** Validate only the current step's fields before advancing. */
  async function next() {
    const fields: Record<Step, (keyof PublicRegistrationInput)[]> = {
      0: ['fullName', 'nic', 'dob', 'gender', 'maritalStatus'],
      1: ['phone', 'whatsapp', 'email', 'district', 'city', 'address'],
      2: [],
    }
    if (await trigger(fields[step] as never)) setStep((s) => Math.min(2, s + 1) as Step)
  }

  async function onSubmit(values: PublicRegistrationInput) {
    setSubmitting(true)
    setFormError(null)
    try {
      const token = await getTurnstileToken()
      const result = await apiFetch<{ reference: string }>(`/api/public/links/${slug}/register`, {
        method: 'POST',
        body: JSON.stringify({ ...values, turnstileToken: token }),
      })
      setReference(result.reference)
    } catch (e) {
      // A conflict here means the link closed or filled while the form was open
      // — the server is authoritative, so surface its wording verbatim.
      setFormError(
        e instanceof ApiClientError ? e.message : 'Could not submit. Please check your connection.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (reference) {
    return (
      <Notice
        icon={<CheckCircle2 className="h-8 w-8 text-emerald-600" />}
        title="Registration received"
        body="The agency will review your details and contact you. Keep your reference number."
      >
        <div className="mt-4 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)] px-4 py-3">
          <p className="text-xs text-[var(--color-muted-foreground)]">Your reference</p>
          <p className="font-mono text-lg font-semibold tracking-wider">{reference}</p>
        </div>
      </Notice>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stepper step={step} />

      {formError && (
        <div
          role="alert"
          className="mb-4 rounded-md bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
        >
          {formError}
        </div>
      )}

      {step === 0 && (
        <div className="space-y-4">
          <Field label="Full name" htmlFor="fullName" error={errors.fullName?.message} required>
            <Input id="fullName" autoFocus autoComplete="name" {...register('fullName')} />
          </Field>

          <Field
            label="NIC number"
            htmlFor="nic"
            error={errors.nic?.message}
            hint="e.g. 912345678V"
          >
            <Input id="nic" inputMode="text" {...register('nic')} />
          </Field>

          <Field label="Date of birth" htmlFor="dob" error={errors.dob?.message}>
            <Input id="dob" type="date" {...register('dob')} />
          </Field>

          <Field label="Gender" htmlFor="gender" error={errors.gender?.message}>
            <Select id="gender" {...register('gender')}>
              <option value="">Select…</option>
              {GENDERS.map((g) => (
                <option key={g} value={g}>
                  {g === 'male' ? 'Male' : 'Female'}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Marital status"
            htmlFor="maritalStatus"
            error={errors.maritalStatus?.message}
          >
            <Select id="maritalStatus" {...register('maritalStatus')}>
              <option value="">Select…</option>
              {MARITAL_STATUSES.map((m) => (
                <option key={m} value={m}>
                  {m[0]!.toUpperCase() + m.slice(1)}
                </option>
              ))}
            </Select>
          </Field>

          {shows('physical') && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Height (cm)" htmlFor="heightCm" error={errors.heightCm?.message}>
                <Input id="heightCm" type="number" inputMode="numeric" {...register('heightCm')} />
              </Field>
              <Field label="Weight (kg)" htmlFor="weightKg" error={errors.weightKg?.message}>
                <Input id="weightKg" type="number" inputMode="numeric" {...register('weightKg')} />
              </Field>
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <Field
            label="Mobile number"
            htmlFor="phone"
            error={errors.phone?.message}
            hint="e.g. 077 123 4567"
            required
          >
            <Input
              id="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              {...register('phone')}
            />
          </Field>

          <Field
            label="WhatsApp number"
            htmlFor="whatsapp"
            error={errors.whatsapp?.message}
            hint="If different"
          >
            <Input id="whatsapp" type="tel" inputMode="tel" {...register('whatsapp')} />
          </Field>

          <Field label="Email" htmlFor="email" error={errors.email?.message}>
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              {...register('email')}
            />
          </Field>

          <Field label="District" htmlFor="district" error={errors.district?.message}>
            <Select id="district" {...register('district')}>
              <option value="">Select…</option>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="City / town" htmlFor="city" error={errors.city?.message}>
            <Input id="city" {...register('city')} />
          </Field>

          <Field label="Address" htmlFor="address" error={errors.address?.message}>
            <Input id="address" {...register('address')} />
          </Field>

          {shows('emergency') && (
            <>
              <Field
                label="Emergency contact name"
                htmlFor="emergencyName"
                error={errors.emergencyName?.message}
              >
                <Input id="emergencyName" {...register('emergencyName')} />
              </Field>
              <Field
                label="Emergency contact phone"
                htmlFor="emergencyPhone"
                error={errors.emergencyPhone?.message}
              >
                <Input
                  id="emergencyPhone"
                  type="tel"
                  inputMode="tel"
                  {...register('emergencyPhone')}
                />
              </Field>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <Field
            label="Countries you’d like to work in"
            htmlFor="preferredCountries"
            error={errors.preferredCountries?.message}
          >
            <Controller
              control={control}
              name="preferredCountries"
              render={({ field }) => (
                <MultiSelect
                  id="preferredCountries"
                  options={countryOptions}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  placeholder="Select countries…"
                  max={10}
                />
              )}
            />
          </Field>

          <Field
            label="Jobs you can do"
            htmlFor="jobCategoryIds"
            error={errors.jobCategoryIds?.message}
          >
            <Controller
              control={control}
              name="jobCategoryIds"
              render={({ field }) => (
                <MultiSelect
                  id="jobCategoryIds"
                  options={jobCategoryOptions}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  placeholder="Select jobs…"
                  max={10}
                />
              )}
            />
          </Field>

          {shows('education') && (
            <>
              <Field
                label="Education"
                htmlFor="educationLevel"
                error={errors.educationLevel?.message}
              >
                <Select id="educationLevel" {...register('educationLevel')}>
                  <option value="">Select…</option>
                  {educationOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field
                label="Years of experience"
                htmlFor="experienceYears"
                error={errors.experienceYears?.message}
              >
                <Input
                  id="experienceYears"
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  min="0"
                  {...register('experienceYears')}
                />
              </Field>
            </>
          )}

          {shows('passport') && (
            <>
              <Field
                label="Do you have a passport?"
                htmlFor="passportStatus"
                error={errors.passportStatus?.message}
              >
                <Select id="passportStatus" {...register('passportStatus')}>
                  {PASSPORT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {PASSPORT_STATUS_LABELS[s]}
                    </option>
                  ))}
                </Select>
              </Field>

              {passportStatus !== 'none' && (
                <>
                  <Field
                    label="Passport number"
                    htmlFor="passportNo"
                    error={errors.passportNo?.message}
                    required={passportStatus === 'in_hand'}
                  >
                    <Input id="passportNo" {...register('passportNo')} />
                  </Field>
                  <Field
                    label="Passport expiry"
                    htmlFor="passportExpiry"
                    error={errors.passportExpiry?.message}
                  >
                    <Input id="passportExpiry" type="date" {...register('passportExpiry')} />
                  </Field>
                </>
              )}
            </>
          )}

          {shows('languages') && (
            <div>
              <p className="mb-2 text-sm font-medium">Languages</p>
              <div className="space-y-2">
                {languages.fields.map((f, i) => (
                  <div key={f.id} className="flex gap-2">
                    <Select className="flex-1" {...register(`languages.${i}.language` as const)}>
                      {LANGUAGES.map((l) => (
                        <option key={l} value={l}>
                          {l[0]!.toUpperCase() + l.slice(1)}
                        </option>
                      ))}
                    </Select>
                    <Select className="flex-1" {...register(`languages.${i}.proficiency` as const)}>
                      {LANGUAGE_PROFICIENCIES.map((p) => (
                        <option key={p} value={p}>
                          {p[0]!.toUpperCase() + p.slice(1)}
                        </option>
                      ))}
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => languages.remove(i)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => languages.append({ language: 'english', proficiency: 'basic' })}
                >
                  <Plus className="h-4 w-4" /> Add language
                </Button>
              </div>
            </div>
          )}

          {shows('foreign_experience') && (
            <div>
              <p className="mb-2 text-sm font-medium">Foreign work experience</p>
              <div className="space-y-2">
                {foreignExperience.fields.map((f, i) => (
                  <div
                    key={f.id}
                    className="space-y-2 rounded-md border border-[var(--color-border)] p-2"
                  >
                    <Select {...register(`foreignExperience.${i}.countryCode` as const)}>
                      {countryOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                    <div className="flex gap-2">
                      <Input
                        className="flex-1"
                        placeholder="Role"
                        {...register(`foreignExperience.${i}.role` as const)}
                      />
                      <Input
                        className="w-24"
                        type="number"
                        inputMode="decimal"
                        step="0.5"
                        placeholder="Years"
                        {...register(`foreignExperience.${i}.years` as const)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => foreignExperience.remove(i)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    foreignExperience.append({ countryCode: 'SA', role: '', years: 1 })
                  }
                >
                  <Plus className="h-4 w-4" /> Add experience
                </Button>
              </div>
            </div>
          )}

          <Field
            label="Expected salary"
            htmlFor="expectedSalary"
            error={errors.expectedSalary?.message}
          >
            <div className="flex gap-2">
              <Input
                id="expectedSalary"
                type="number"
                inputMode="numeric"
                min="0"
                className="flex-1"
                {...register('expectedSalary')}
              />
              <Select className="w-28" {...register('salaryCurrency')}>
                <option value="">—</option>
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </div>
          </Field>

          {/* Consent is captured at intake (PLAN §11) and is what every later
              campaign filters on — so it's asked plainly, not buried. */}
          <fieldset className="rounded-md border border-[var(--color-border)] p-3">
            <legend className="px-1 text-sm font-medium">How can we contact you?</legend>
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox {...register('consentSms')} /> SMS
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox {...register('consentWhatsapp')} /> WhatsApp
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox {...register('consentEmail')} /> Email
              </label>
            </div>
            <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
              We’ll only send job news on the channels you tick. You can ask us to stop at any time.
            </p>
          </fieldset>
        </div>
      )}

      <div className="mt-6 flex gap-2">
        {step > 0 && (
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => setStep((s) => (s - 1) as Step)}
          >
            Back
          </Button>
        )}
        {step < 2 ? (
          <Button type="button" className="flex-1" onClick={next}>
            Continue
          </Button>
        ) : (
          <Button type="submit" className="flex-1" loading={submitting}>
            Submit registration
          </Button>
        )}
      </div>
    </form>
  )
}

function Stepper({ step }: { step: Step }) {
  return (
    <div className="mb-6">
      <div className="flex gap-1.5">
        {STEP_TITLES.map((title, i) => (
          <div
            key={title}
            className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]'}`}
          />
        ))}
      </div>
      <p className="mt-2 text-sm font-medium">
        Step {step + 1} of 3 · {STEP_TITLES[step]}
      </p>
    </div>
  )
}

/** Single-column, max-width phone layout — most applicants arrive on a handset. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-[var(--color-muted)] px-4 py-6">
      <div className="mx-auto w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-5 shadow-sm">
        {children}
      </div>
    </div>
  )
}

function Notice({
  icon,
  title,
  body,
  children,
}: {
  icon: ReactNode
  title: string
  body: string
  children?: ReactNode
}) {
  return (
    <div className="py-8 text-center">
      <div className="mb-3 flex justify-center">{icon}</div>
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="mx-auto mt-1 max-w-xs text-sm text-[var(--color-muted-foreground)]">{body}</p>
      {children}
    </div>
  )
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Renders Turnstile invisibly and resolves its token. When no site key is
 * configured (local dev) it resolves undefined — the API mirrors this by only
 * skipping verification in development, so a missing key can never silently
 * disable the captcha in production.
 */
async function getTurnstileToken(): Promise<string | undefined> {
  if (!TURNSTILE_SITE_KEY) return undefined

  await loadTurnstileScript()
  const turnstile = (window as unknown as { turnstile?: TurnstileApi }).turnstile
  if (!turnstile) return undefined

  return new Promise<string | undefined>((resolve) => {
    const container = document.createElement('div')
    container.style.display = 'none'
    document.body.appendChild(container)

    const cleanup = () => container.remove()
    turnstile.render(container, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => {
        cleanup()
        resolve(token)
      },
      'error-callback': () => {
        cleanup()
        resolve(undefined)
      },
    })
  })
}

interface TurnstileApi {
  render: (
    el: HTMLElement,
    options: { sitekey: string; callback: (token: string) => void; 'error-callback': () => void },
  ) => void
}

let turnstileScript: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  turnstileScript ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Turnstile failed to load'))
    document.head.appendChild(script)
  })
  return turnstileScript
}
